<button
  data-testid="carousel-close-btn"
  (click)="close($event)"
  aria-label="Close"
  class="close"
  mat-dialog-close
  mat-icon-button
  >
  <mat-icon>close</mat-icon>
</button>

<swiper-container
  #swiperEl
  [attr.initial-slide]="initialSlide"
  keyboard="true"
  navigation="true"
  pagination="true"
  slides-per-view="1"
  >
  @for (att of attachments; track att; let i = $index) {
    <swiper-slide>
      <div class="slide-header">
        <!-- Markup / Annotate button (only when editable) -->
        @if (isEditable && (att?.['fileType'] === 'picture' || (att?.['fileType'] === 'document' && (att.preview_url || att.thumb_url)))) {
          <button
            data-testid="carousel-edit-btn"
            (click)="openMarkupEditor(att)"
            class="markup-button"
            mat-icon-button
            title="Annotate"
            >
            <mat-icon>edit</mat-icon>
          </button>
        }

        <!-- Download ZIP button - shown only when there are multiple downloadable items -->
        @if (showDownloadAll && isDownloadable(att)) {
          <button
            (click)="downloadAll()"
            [disabled]="downloadingAll"
            [matTooltipDisabled]="downloadingAll"
            class="download-all-button"
            mat-icon-button
            title="Download all"
            >
            @if (!downloadingAll) {
              <mat-icon>archive</mat-icon>
            }
            @if (downloadingAll) {
              <mat-spinner color="primary" diameter="20" mode="indeterminate"></mat-spinner>
            }
          </button>
        }
        <!-- Photo proof (GeoStamp): details panel toggle, only for photos that carry a proof -->
        @if (hasProof(att)) {
          <button
            data-testid="proof-button"
            (click)="toggleProofPanel()"
            class="proof-button"
            [class.active]="proofPanelOpen"
            mat-icon-button
            title="Photo details"
            >
            <mat-icon>info_outline</mat-icon>
          </button>
        }
        <!-- Individual download button (pictures & videos) -->
        @if (isDownloadable(att)) {
          <button
            (click)="download(att)"
            aria-label="Download {{ att.original_name }}"
            class="download-button"
            mat-icon-button
            title="{{ att?.fileType === 'picture' ? ('Download ' + att?.original_name || 'Download image') : 'Download video' }}"
            >
            <mat-icon>download</mat-icon>
          </button>
        }
      </div>
      <div class="slide-content" [style.--blur-src]="(att.thumb_url || att.preview || att.preview_url) ? 'url(' + (att.thumb_url || att.preview || att.preview_url) + ')' : 'none'">
        @if (att?.['fileType'] === 'picture') {
          <img
            [src]="getSlideImageSrc(att)"
            [attr.alt]="att.original_name || 'Image ' + (i + 1)"
            class="slide-image"
            />
          @if (proofPanelOpen && hasProof(att)) {
            <app-photo-proof-panel
              [picture]="att"
              [thresholdM]="verifiedThresholdM"
              [showingOriginal]="!!showOriginal[att.id]"
              (toggleOriginal)="toggleOriginal(att)"
              ></app-photo-proof-panel>
          }
        } @else {
          @if (att?.['fileType'] === 'document') {
            @if (isPdf(att)) {
              @if (pdfPageState[att.id]?.totalPages > 1) {
                <!-- Multi-page PDF: show rendered page image with navigation -->
                <img
                  [src]="getSlideImageSrc(att)"
                  [attr.alt]="att.original_name || 'Document ' + (i + 1)"
                  class="slide-image"
                  />
                <div class="pdf-page-nav">
                  <button mat-icon-button
                          [disabled]="pdfPageState[att.id].currentPage <= 1 || pdfPageState[att.id].loading"
                          (click)="goToPdfPage(att, pdfPageState[att.id].currentPage - 1)">
                    <mat-icon>chevron_left</mat-icon>
                  </button>
                  <span class="pdf-page-indicator">
                    @if (pdfPageState[att.id].loading) {
                      <mat-spinner diameter="16"></mat-spinner>
                    }
                    @if (!pdfPageState[att.id].loading) {
                      @if (editingPageNumber) {
                        <span class="page-jump">
                          <input class="page-jump-input"
                                 type="number"
                                 [value]="pageJumpInput"
                                 (input)="pageJumpInput = $any($event.target).value"
                                 (keydown.enter)="submitPageJump(att)"
                                 (keydown.escape)="cancelPageJump()"
                                 (blur)="submitPageJump(att)"
                                 min="1"
                                 [max]="pdfPageState[att.id].totalPages"
                                 data-testid="carousel-page-jump-input"
                                 #pageJumpRef>
                          <span>/ {{ pdfPageState[att.id].totalPages }}</span>
                        </span>
                      } @else {
                        <span class="page-label" (click)="startPageJump(att)" data-testid="carousel-page-label" title="Click to jump to page">
                          Page {{ pdfPageState[att.id].currentPage }} / {{ pdfPageState[att.id].totalPages }}
                        </span>
                      }
                    }
                  </span>
                  <button mat-icon-button
                          [disabled]="pdfPageState[att.id].currentPage >= pdfPageState[att.id].totalPages || pdfPageState[att.id].loading"
                          (click)="goToPdfPage(att, pdfPageState[att.id].currentPage + 1)">
                    <mat-icon>chevron_right</mat-icon>
                  </button>
                </div>
              } @else if (pdfPageState[att.id]) {
                <!-- Single-page PDF or still loading: show rendered image -->
                <img
                  [src]="getSlideImageSrc(att)"
                  [attr.alt]="att.original_name || 'Document ' + (i + 1)"
                  class="slide-image"
                  />
                @if (pdfPageState[att.id].loading) {
                  <div class="pdf-loading-overlay">
                    <mat-spinner diameter="32"></mat-spinner>
                  </div>
                }
              } @else {
                <!-- PDF not yet initialized — show loading instead of iframe to prevent flash -->
                <div class="pdf-loading-overlay">
                  <mat-spinner diameter="32"></mat-spinner>
                </div>
              }
            } @else {
              <!-- Non-PDF document (Excel, Word, etc.): show preview thumbnail -->
              <img
                [src]="att.preview_url || att.thumb_url || '/assets/icons/file.png'"
                [attr.alt]="att.original_name || 'Document ' + (i + 1)"
                class="slide-image"
                />
            }
          } @else {
            @if (att.thumb_url) {
              <mux-player
                [attr.controls]="true"
                [attr.metadata-duration]="att?.['duration']"
                [attr.playback-id]="att?.['playback_id']"
                [attr.poster]="att.thumb_url"
                class="swiper-lazy swiper-no-swiping"
                stream-type="on-demand"
                style="width: auto; height: 90%;"
                >
              </mux-player>
            } @else if (att.original_url) {
              <!-- Video stored as a plain attachment (S3), not a Mux video -->
              <video
                [src]="att.original_url"
                class="slide-video swiper-no-swiping"
                controls
                playsinline
                preload="metadata"
                >
              </video>
            } @else {
              <div class="no-video swiper-lazy swiper-no-swiping flex flex-col justify-center items-center gap-[20px]">
                <img alt="no-video" class="no-video-image" src="assets/images/png/video-processing.png">
                <div class="text text-type-3">
                  Your video is still processing
                </div>
              </div>
            }
          }
        }
      </div>
    </swiper-slide>
  }
</swiper-container>

<!-- Markup Editor Overlay -->
@if (showMarkupEditor) {
  <app-markup-editor
    [config]="markupEditorConfig"
    (closed)="closeMarkupEditor()"
    (saved)="onMarkupSaved($event)">
  </app-markup-editor>
}
